import React, {useReducer, useEffect, useState, useRef} from 'react'
import {withRouter} from 'react-router-dom'
import {useDispatch, useSelector} from 'react-redux'
import {
  withStyles,
  styled,
  Button as MuiButton,
  FormControl as MuiFormControl,
  InputLabel as MuiInputLabel,
  MenuItem as MuiMenuItem,
  OutlinedInput as MuiOutlinedInput,
  Select as MuiSelect,
  TextField as MuiTextField,
  FormControlLabel as MuiFormControlLabel,
  Checkbox as MuiCheckbox,
  Typography
} from '@material-ui/core'
import BackButton from 'ui/components/BackButton'
import DeleteButton from './DeleteButton'
import {reducer, init, updateField} from './state'
import {useLabelWidth} from 'ui/hooks'
import {patchUser, createUser, sendInviteEmail, resetUserCreationStatus, resetButtonState} from 'users/store/actions'
import {useCurrentUser} from 'users/hooks'
import Applications from './Applications'

const Content = styled('form')({})

const Header = styled('div')({
  marginBottom: 20,
  display: 'flex',
  justifyContent: 'space-between'
})

const Title = styled('div')({
  fontSize: 24,
  fontWeight: 500
})

const ErrorMessage = styled(Typography)(({theme}) => ({
  color: theme.palette.error.main,
  marginBottom: 16
}))

const Body = styled('div')(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 8px 1fr',
  maxWidth: 800,

  // default text fields
  '& > *': {
    gridColumn: '1 / 4'
  },

  [theme.breakpoints.up('md')]: {
    // first name field
    '& > :nth-child(1)': {
      gridColumn: '1'
    },

    // last name field
    '& > :nth-child(2)': {
      gridColumn: '3'
    }
  }
}))

const Footer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 20
})

const Actions = styled('div')({
  display: 'grid',
  gridAutoFlow: 'column',
  gridColumnGap: 10
})

const Label = withStyles(theme => ({
  root: {
    marginLeft: 0
  },
  label: {
    fontSize: 14,
    fontWeight: 500
  }
}))(MuiFormControlLabel)

const renderCheckBox = checked => <MuiCheckbox checked={checked} />

const Form = ({userData, staticContext, history, ...props}) => {
  const currentUser = useCurrentUser()
  const [state, dispatch] = useReducer(reducer, userData, init)
  const [labelWidth, labelRef] = useLabelWidth('Role')
  const dispatchRedux = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {user} = state

  const title = user.id ? 'Edit User' : 'New User'
  const buttonTitle = user.id ? 'Update User' : 'Save User'

  // Get error message if there is one
  const error = useSelector(state => {
    if (user.id) return null
    // For new users, get the error from the users state
    return state.users && state.users.error ? state.users.error.message : null
  })

  const roleInput = (
    <MuiOutlinedInput id='role' name='role' labelWidth={labelWidth} />
  )

  const handleChange = (name, value) => dispatch(updateField(name, value))

  // Add state to listen for success/failure
  const userCreationStatus = useSelector(state => {
    // For existing users (editing), we don't need to track this
    if (user.id) return null
    
    // For CRUD operations done by the admin, we need to look at users.users.userCreationStatus
    const status = state.users.users.userCreationStatus
    return status
  })

  // Add a tracking effect for userCreationStatus
  useEffect(() => {
    // Force redirection if status is 'succeeded'
    if (userCreationStatus === 'succeeded') {
      // Success handling happens in the dedicated effect
    }
  }, [userCreationStatus, history])

  // Create a local state for button disabled
  const [buttonDisabled, setButtonDisabled] = useState(false)

  // Store timeout references to clean them up
  const timeoutRefs = useRef([])

  // Effect to handle redirect after successful creation
  useEffect(() => {
    if (userCreationStatus === 'succeeded') {
      // Clear any pending timeouts
      timeoutRefs.current.forEach(clearTimeout)
      timeoutRefs.current = []
      
      // Clear form state before redirecting
      setIsSubmitting(false)
      setButtonDisabled(false)
      
      // Clear our creation tracking flag
      if (window.userCreationInProgress) {
        delete window.userCreationInProgress
      }
      
      // Redirect to user list
      history.push('/users')
      
    } else if (userCreationStatus === 'failed') {
      //console.log('User creation failed, resetting form state')
      
      // Reset the submitting state when we get an error
      setIsSubmitting(false)
      setButtonDisabled(false)
      
      // Clear our creation tracking flag
      if (window.userCreationInProgress) {
        delete window.userCreationInProgress
      }
      
      //console.log('Form state reset after error')
    }
  }, [userCreationStatus, history])
  
  // Also listen for error messages directly, as a backup to reset the button
  const currentError = useSelector(state => state.users && state.users.error)
  
  // Listen for the special button reset signal
  const buttonResetSignal = useSelector(state => state.users && state.users.buttonResetSignal)
  
  useEffect(() => {
    if (currentError) {
      setIsSubmitting(false)
      setButtonDisabled(false)
    }
  }, [currentError])
  
  // Reset button when we get the special signal
  useEffect(() => {
    if (buttonResetSignal) {
      setIsSubmitting(false)
      setButtonDisabled(false)
    }
  }, [buttonResetSignal])

  // Reset creation status when component mounts and cleans up when unmounting
  useEffect(() => {
    // Only reset if we're creating a new user
    if (!user.id) {
      dispatchRedux(resetUserCreationStatus())
      
      // When navigating to New User form, always clear any redirect flag to prevent accidental redirects
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem('redirectToUsersList')
      }
    }
    
    // Cleanup function to reset state when component unmounts
    return () => {
      // Clear any pending timeouts
      timeoutRefs.current.forEach(clearTimeout)
      timeoutRefs.current = []
      
      if (!user.id) {
        dispatchRedux(resetUserCreationStatus())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSave = () => {
    // Trim email to prevent issues with leading/trailing spaces
    const userWithTrimmedEmail = {
      ...user,
      email: user.email ? user.email.trim() : user.email
    }
    
    if (user.id) {
      // For existing users, just save and redirect
      dispatchRedux(patchUser(userWithTrimmedEmail))
      history.push('/users')
    } else {
      // For new users, dispatch action but don't redirect yet
      setIsSubmitting(true)
      setButtonDisabled(true)
      
      // Reset any previous error state first
      dispatchRedux(resetUserCreationStatus())
      
      // Small delay to ensure state is updated
      setTimeout(() => {
        // Store reference to creation in progress
        window.userCreationInProgress = true
        
        // Create the user
        dispatchRedux(createUser({...userWithTrimmedEmail, sendInviteEmail: true}))
        
        // Safety timeout to re-enable the button after 5 seconds regardless of redux state
        // This ensures the button doesn't stay disabled forever if something goes wrong
        const timeoutId = setTimeout(() => {
          setButtonDisabled(false)
          setIsSubmitting(false)
          
          // Clean up our creation flag if it's still set after 5 seconds
          if (window.userCreationInProgress) {
            delete window.userCreationInProgress
          }
        }, 3000)
        
        // Store timeout reference for cleanup
        timeoutRefs.current.push(timeoutId)
      }, 10)
      
      // Redirect will happen in the useEffect after success
    }
  }

  const resendInvite = () => dispatchRedux(sendInviteEmail(user.id))

  const disabled = user.id === currentUser.id

  let resendInviteButton
  if (user.id && !user.lastSignedIn) {
    resendInviteButton = (
      <MuiButton color='primary' onClick={resendInvite}>
        Resend Invite Email
      </MuiButton>
    )
  }

  const hasLeadNotificationSettings =
    user.settings && user.settings.leadNotifications
  const isAdminOrVendor = user.role === 'vendor' || user.role === 'admin'
  const isCurrentUserAdmin = currentUser.role === 'admin'

  return (
    <Content {...props}>
      <Header>
        <Title>{title}</Title>
        {resendInviteButton}
      </Header>
      {error && (
        <ErrorMessage variant="body1">
          {error}
        </ErrorMessage>
      )}
      <Body>
        <MuiTextField
          label='First Name'
          defaultValue={user.firstName}
          onChange={e => handleChange('firstName', e.target.value)}
          variant='outlined'
          margin='dense'
        />
        <MuiTextField
          label='Last Name'
          defaultValue={user.lastName}
          onChange={e => handleChange('lastName', e.target.value)}
          variant='outlined'
          margin='dense'
        />
        <MuiTextField
          label='Company Name'
          defaultValue={user.companyName}
          onChange={e => handleChange('companyName', e.target.value)}
          variant='outlined'
          margin='dense'
        />
        <MuiTextField
          label='Job Title'
          defaultValue={user.jobTitle}
          onChange={e => handleChange('jobTitle', e.target.value)}
          variant='outlined'
          margin='dense'
        />
        <MuiTextField
          label='Email'
          defaultValue={user.email}
          onChange={e => handleChange('email', e.target.value)}
          variant='outlined'
          margin='dense'
        />
        <MuiTextField
          label='Phone'
          defaultValue={user.phone}
          onChange={e => handleChange('phone', e.target.value)}
          variant='outlined'
          margin='dense'
        />
        <MuiFormControl variant='outlined' margin='dense' disabled={disabled}>
          <MuiInputLabel ref={labelRef} htmlFor='role'>
            Role
          </MuiInputLabel>
          <MuiSelect
            name='role'
            value={user.role}
            onChange={e => handleChange('role', e.target.value)}
            input={roleInput}
          >
            <MuiMenuItem value='admin'>Admin</MuiMenuItem>
            <MuiMenuItem value='vendor'>Vendor</MuiMenuItem>
            <MuiMenuItem value='user'>User</MuiMenuItem>
          </MuiSelect>
        </MuiFormControl>
        <Applications ids={user.applications} role={user.role} />
        {isCurrentUserAdmin && isAdminOrVendor && (
          <Label
            label={
              <p>
                Allow user to be notified when a lead is generated
                <span style={{color: '#808080'}}>
                  {' '}
                  (Only applies to Premium Systems)
                </span>
              </p>
            }
            control={renderCheckBox(hasLeadNotificationSettings)}
            onClick={() =>
              handleChange('settings', {
                leadNotifications: !hasLeadNotificationSettings
              })
            }
            disabled={false}
            {...props}
          />
        )}
      </Body>
      <Footer>
        <Actions>
          <DeleteButton id={user.id} disabled={disabled} />
          {!user.id && (
            <MuiButton 
              color="default" 
              onClick={() => {
                console.log('Manual reset clicked')
                setIsSubmitting(false)
                setButtonDisabled(false)
                dispatchRedux(resetButtonState())
                dispatchRedux(resetUserCreationStatus())
              }}
            >
              Reset Form
            </MuiButton>
          )}
        </Actions>
        <Actions>
          <BackButton />
          <MuiButton 
            color='primary' 
            variant='contained' 
            onClick={handleSave}
            disabled={buttonDisabled}
          >
            {isSubmitting && userCreationStatus !== 'failed' ? 'Saving...' : buttonTitle}
          </MuiButton>
        </Actions>
      </Footer>
    </Content>
  )
}

export default withRouter(Form)
